import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

export default function BrandingSettingsPage() {
  const [appName, setAppName]     = useState("");
  const [logoUrl, setLogoUrl]     = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(null); // "logo" | "favicon" | null
  const [msg, setMsg]             = useState(null);

  const logoInputRef    = useRef(null);
  const faviconInputRef = useRef(null);

  // Chatwoot
  const [chatwootToken, setChatwootToken] = useState("");
  const [chatwootSaving, setChatwootSaving] = useState(false);

  // SnapTrade
  const [snaptradeClientIdMasked, setSnaptradeClientIdMasked]       = useState("");
  const [snaptradeConsumerKeyMasked, setSnaptradeConsumerKeyMasked] = useState("");
  const [snaptradeClientIdDraft, setSnaptradeClientIdDraft]         = useState("");
  const [snaptradeConsumerKeyDraft, setSnaptradeConsumerKeyDraft]   = useState("");
  const [snaptradeSaving, setSnaptradeSaving]                       = useState(false);

  // Anthropic
  const [anthropicMasked, setAnthropicMasked] = useState("");
  const [anthropicDraft, setAnthropicDraft]   = useState("");
  const [anthropicSaving, setAnthropicSaving] = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchBranding = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/branding");
      setAppName(res.data.app_name || "");
      setLogoUrl(res.data.logo_url);
      setFaviconUrl(res.data.favicon_url);
      setChatwootToken(res.data.chatwoot_token || "");
      setSnaptradeClientIdMasked(res.data.snaptrade_client_id_masked || "");
      setSnaptradeConsumerKeyMasked(res.data.snaptrade_consumer_key_masked || "");
      setAnthropicMasked(res.data.anthropic_key_masked || "");
    } catch {
      flash("Failed to load admin settings.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);

  const saveAppName = async () => {
    if (!appName.trim()) {
      flash("App name is required.", false);
      return;
    }
    setSaving(true);
    try {
      await axios.post("/admin/branding", { app_name: appName.trim() });
      flash("App name updated. Changes are live on next page load — no restart needed.");
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update app name.", false);
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`/admin/branding/${field}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Bust the browser cache so the new image shows immediately below.
      const bust = `?v=${Date.now()}`;
      if (field === "logo") setLogoUrl(`/brand-logo.png${bust}`);
      else setFaviconUrl(`/brand-favicon.ico${bust}`);
      flash(`${field === "logo" ? "Logo" : "Favicon"} updated. Changes are live on next page load — no restart needed.`);
    } catch (e) {
      flash(e?.response?.data?.error || `Failed to upload ${field}.`, false);
    } finally {
      setUploading(null);
    }
  };

  const saveChatwoot = async () => {
    setChatwootSaving(true);
    try {
      await axios.post("/admin/branding/chatwoot", { chatwoot_token: chatwootToken.trim() });
      flash("Chatwoot token updated. Changes are live on next page load — no restart needed.");
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update Chatwoot token.", false);
    } finally {
      setChatwootSaving(false);
    }
  };

  const saveSnaptrade = async () => {
    if (!snaptradeClientIdDraft.trim() || !snaptradeConsumerKeyDraft.trim()) {
      flash("Enter both Client ID and Consumer Key.", false);
      return;
    }
    setSnaptradeSaving(true);
    try {
      const res = await axios.post("/admin/branding/snaptrade", {
        client_id: snaptradeClientIdDraft.trim(),
        consumer_key: snaptradeConsumerKeyDraft.trim(),
      });
      setSnaptradeClientIdMasked(res.data.snaptrade_client_id_masked || "");
      setSnaptradeConsumerKeyMasked(res.data.snaptrade_consumer_key_masked || "");
      setSnaptradeClientIdDraft("");
      setSnaptradeConsumerKeyDraft("");
      flash("SnapTrade credentials updated. Takes effect on the next API call — no restart needed.");
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update SnapTrade credentials.", false);
    } finally {
      setSnaptradeSaving(false);
    }
  };

  const saveAnthropicKey = async () => {
    if (!anthropicDraft.trim()) {
      flash("Enter an API key first.", false);
      return;
    }
    setAnthropicSaving(true);
    try {
      const res = await axios.post("/settings/admin/anthropic-key", { api_key: anthropicDraft.trim() });
      setAnthropicMasked(res.data.masked_key || "");
      setAnthropicDraft("");
      flash("Anthropic API key updated.");
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update key.", false);
    } finally {
      setAnthropicSaving(false);
    }
  };

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Branding &amp; Integrations</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          App identity and third-party integration keys. Changes apply on next page load / API call — no deploy or restart needed.
        </p>
      </div>

      {/* App name */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>App Name</div>
        <div style={sectionHint}>
          Shown in the header, footer copyright line, and browser tab title.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={appName}
            onChange={e => setAppName(e.target.value)}
            disabled={loading}
            placeholder="Fincrest Systems"
            maxLength={100}
            style={{ ...smallInput, width: 280 }}
          />
          <button type="button" onClick={saveAppName} disabled={saving || loading} style={btnPrimary}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Logo */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>Logo</div>
        <div style={sectionHint}>PNG, JPEG, GIF, or WEBP, up to 2MB. Shown in the header.</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div style={previewBox}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo preview" style={{ maxWidth: "100%", maxHeight: "100%" }} />
              : <span style={{ fontSize: 11, color: "#999" }}>Default logo</span>}
          </div>
          <div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={e => uploadImage("logo", e.target.files[0])}
            />
            <button type="button" onClick={() => logoInputRef.current.click()} disabled={uploading === "logo"} style={btnSecondary}>
              {uploading === "logo" ? "Uploading…" : "Upload New Logo"}
            </button>
          </div>
        </div>
      </div>

      {/* Favicon */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>Favicon</div>
        <div style={sectionHint}>ICO, PNG, GIF, or WEBP, up to 2MB. Shown in the browser tab.</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div style={{ ...previewBox, width: 48, height: 48 }}>
            {faviconUrl
              ? <img src={faviconUrl} alt="Favicon preview" style={{ maxWidth: "100%", maxHeight: "100%" }} />
              : <span style={{ fontSize: 9, color: "#999", textAlign: "center" }}>Default</span>}
          </div>
          <div>
            <input
              ref={faviconInputRef}
              type="file"
              accept="image/*,.ico"
              style={{ display: "none" }}
              onChange={e => uploadImage("favicon", e.target.files[0])}
            />
            <button type="button" onClick={() => faviconInputRef.current.click()} disabled={uploading === "favicon"} style={btnSecondary}>
              {uploading === "favicon" ? "Uploading…" : "Upload New Favicon"}
            </button>
          </div>
        </div>
      </div>

      {/* Chatwoot */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>Chatwoot Support Widget</div>
        <div style={sectionHint}>
          Website Token from your Chatwoot Inbox Settings → Configuration. Leave blank to hide the support widget.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={chatwootToken}
            onChange={e => setChatwootToken(e.target.value)}
            disabled={loading}
            placeholder="Chatwoot Website Token"
            style={{ ...smallInput, width: 280, fontFamily: "monospace" }}
          />
          <button type="button" onClick={saveChatwoot} disabled={chatwootSaving || loading} style={btnPrimary}>
            {chatwootSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* SnapTrade */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>SnapTrade App Credentials</div>
        <div style={sectionHint}>
          Client ID and Consumer Key from your SnapTrade developer dashboard. Used to sign every brokerage-linking request. Stored encrypted; never shown in full once saved.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: "#6c757d" }}>
            Client ID: <span style={{ fontFamily: "monospace" }}>{snaptradeClientIdMasked || "Not set — using backend/.env fallback"}</span>
          </div>
          <div style={{ fontSize: 11, color: "#6c757d" }}>
            Consumer Key: <span style={{ fontFamily: "monospace" }}>{snaptradeConsumerKeyMasked || "Not set — using backend/.env fallback"}</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
            <input
              type="text"
              placeholder="New Client ID"
              value={snaptradeClientIdDraft}
              onChange={e => setSnaptradeClientIdDraft(e.target.value)}
              autoComplete="off"
              style={{ ...smallInput, width: 220, fontFamily: "monospace" }}
            />
            <input
              type="password"
              placeholder="New Consumer Key"
              value={snaptradeConsumerKeyDraft}
              onChange={e => setSnaptradeConsumerKeyDraft(e.target.value)}
              autoComplete="off"
              style={{ ...smallInput, width: 220, fontFamily: "monospace" }}
            />
            <button type="button" onClick={saveSnaptrade} disabled={snaptradeSaving} style={btnPrimary}>
              {snaptradeSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Anthropic */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>AI Assistant — Anthropic API Key</div>
        <div style={sectionHint}>
          Used by the chat support agent for all users. Stored encrypted; never shown in full once saved.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontFamily: "monospace", fontSize: 13, color: "#495057", background: "#f8f9fa", padding: "7px 12px", borderRadius: 6, minWidth: 160 }}>
            {anthropicMasked || "Not set — using backend/.env fallback"}
          </div>
          <input
            type="password"
            placeholder="sk-ant-..."
            value={anthropicDraft}
            onChange={e => setAnthropicDraft(e.target.value)}
            autoComplete="off"
            style={{ ...smallInput, width: 260 }}
          />
          <button type="button" onClick={saveAnthropicKey} disabled={anthropicSaving} style={btnPrimary}>
            {anthropicSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const tile         = { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#2c3e50", marginBottom: 4 };
const sectionHint  = { fontSize: 12, color: "#888", marginBottom: 12 };
const smallInput   = { padding: "7px 12px", borderRadius: 8, border: "1px solid #dee2e6", fontSize: 13 };
const btnPrimary   = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const previewBox   = { width: 96, height: 64, border: "1px solid #dee2e6", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f9fa", overflow: "hidden", flexShrink: 0 };
